import React, { useState, useEffect, useCallback } from 'react';
import { gstReturnAPI, formatCurrency } from '../services/api';
import { FileText, Download, ChevronDown, ChevronUp, Save, ToggleLeft, ToggleRight, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

function Section({ title, defaultOpen = false, children, count, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testId}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition">
        <div className="flex items-center gap-3">
          <h3 className="font-heading font-semibold text-slate-800">{title}</h3>
          {count !== undefined && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{count}</span>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="border-t border-slate-200">{children}</div>}
    </div>
  );
}

export default function GSTReturnsPage() {
  const [tab, setTab] = useState('gstr1');
  const [period, setPeriod] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [gstr1, setGstr1] = useState(null);
  const [gstr3b, setGstr3b] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [savedReports, setSavedReports] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load settings and saved reports on mount
  useEffect(() => {
    api.get('/gst-returns/settings').then(r => {
      setAutoGenerate(r.data?.auto_generate !== false);
    }).catch(() => {});
    api.get('/gst-returns/reports').then(r => {
      setSavedReports(r.data?.reports || []);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (period) params.period = period;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;

    if (tab === 'gstr1') {
      gstReturnAPI.gstr1(params).then(r => setGstr1(r.data)).catch(console.error).finally(() => setLoading(false));
    } else {
      gstReturnAPI.gstr3b(params).then(r => setGstr3b(r.data)).catch(console.error).finally(() => setLoading(false));
    }
  }, [tab, period, dateFrom, dateTo]);

  // Auto-generate when filters change (if toggle is ON)
  useEffect(() => {
    if (autoGenerate) load();
  }, [load, autoGenerate]);

  const toggleAutoGenerate = async () => {
    const newVal = !autoGenerate;
    setAutoGenerate(newVal);
    try {
      await api.put('/gst-returns/settings', { auto_generate: newVal });
      toast.success(newVal ? 'Auto-generate enabled' : 'Auto-generate disabled');
    } catch { toast.error('Failed to save setting'); }
  };

  const handleManualGenerate = () => {
    load();
    toast.success('Report generated');
  };

  const handleSaveReport = async () => {
    setSaving(true);
    try {
      await api.post('/gst-returns/reports', {
        report_type: tab,
        period,
        date_from: dateFrom,
        date_to: dateTo,
      });
      toast.success('Report saved successfully');
      const r = await api.get('/gst-returns/reports');
      setSavedReports(r.data?.reports || []);
    } catch { toast.error('Failed to save report'); }
    setSaving(false);
  };

  const exportJSON = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="gst-returns-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">GST Returns</h1>
          <p className="text-sm text-slate-500 mt-1">GSTR-1 and GSTR-3B data generation</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Auto-generate toggle */}
          <button onClick={toggleAutoGenerate} data-testid="auto-generate-toggle"
            className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 transition text-sm">
            {autoGenerate
              ? <ToggleRight className="w-5 h-5 text-emerald-600" />
              : <ToggleLeft className="w-5 h-5 text-slate-400" />}
            <span className={autoGenerate ? 'text-emerald-700 font-medium' : 'text-slate-500'}>
              Auto Generate
            </span>
          </button>
          {/* Manual generate */}
          {!autoGenerate && (
            <button onClick={handleManualGenerate} data-testid="manual-generate-btn"
              className="btn-secondary flex items-center gap-2 text-sm">
              <RefreshCw className="w-4 h-4" /> Generate Now
            </button>
          )}
          {/* Save report */}
          <button onClick={handleSaveReport} disabled={saving} data-testid="save-report-btn"
            className="btn-primary flex items-center gap-2 text-sm">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Report'}
          </button>
          {/* View saved reports */}
          <button onClick={() => setShowSaved(!showSaved)} data-testid="view-saved-reports-btn"
            className="btn-secondary flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4" /> Saved ({savedReports.length})
          </button>
        </div>
      </div>

      {/* Saved Reports Panel */}
      {showSaved && savedReports.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="saved-reports-panel">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Saved Reports</h3>
          <div className="divide-y divide-slate-100">
            {savedReports.map(r => (
              <div key={r.report_id} className="flex items-center justify-between py-2">
                <div>
                  <span className="text-sm font-medium text-slate-800">{r.report_type?.toUpperCase()}</span>
                  <span className="text-xs text-slate-400 ml-3">
                    {r.period || `${r.date_from || ''} - ${r.date_to || ''}`}
                  </span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${r.status === 'draft' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {r.status}
                  </span>
                </div>
                <div className="text-xs text-slate-400">
                  {r.created_by_name} - {new Date(r.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs + Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-md p-3">
        <div className="flex gap-1 bg-slate-100 rounded-md p-0.5">
          {['gstr1', 'gstr3b'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${t}`}>
              {t === 'gstr1' ? 'GSTR-1' : 'GSTR-3B'}
            </button>
          ))}
        </div>
        <input type="month" value={period} onChange={e => { setPeriod(e.target.value); setDateFrom(''); setDateTo(''); }}
          className="form-input w-44" data-testid="gst-period" placeholder="Select period" />
        <span className="text-xs text-slate-400">or</span>
        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPeriod(''); }}
          className="form-input w-40" data-testid="gst-date-from" />
        <span className="text-xs text-slate-400">to</span>
        <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPeriod(''); }}
          className="form-input w-40" data-testid="gst-date-to" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : tab === 'gstr1' && gstr1 ? (
        <div className="space-y-4">
          {/* Doc Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              { l: 'Total Invoices', v: gstr1.doc_summary?.total_invoices },
              { l: 'B2B', v: gstr1.doc_summary?.b2b_count },
              { l: 'B2C', v: gstr1.doc_summary?.b2c_count },
              { l: 'Taxable Value', v: `Rs.${formatCurrency(gstr1.doc_summary?.total_taxable)}`, c: 'text-slate-900' },
              { l: 'Total Tax', v: `Rs.${formatCurrency(gstr1.doc_summary?.total_tax)}`, c: 'text-orange-700' },
              { l: 'Total Value', v: `Rs.${formatCurrency(gstr1.doc_summary?.total_value)}`, c: 'text-emerald-700' },
            ].map((m, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-md p-3">
                <p className="text-[10px] font-semibold text-slate-500 uppercase">{m.l}</p>
                <p className={`text-base font-bold font-mono mt-1 ${m.c || 'text-slate-900'}`}>{m.v}</p>
              </div>
            ))}
          </div>

          {/* Export */}
          <div className="flex gap-2">
            <button onClick={() => exportJSON(gstr1, `GSTR1_${period || 'all'}.json`)}
              className="btn-accent flex items-center gap-2 text-sm" data-testid="export-gstr1-json">
              <Download className="w-4 h-4" /> Export GSTR-1 JSON
            </button>
          </div>

          {/* B2B Section */}
          <Section title="B2B Invoices (With GSTIN)" count={gstr1.b2b?.length} defaultOpen={true} testId="gstr1-b2b">
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead><tr><th>Invoice No.</th><th>Date</th><th>Party</th><th>GSTIN</th><th>Place</th><th className="text-right">Taxable</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th><th className="text-right">Total</th></tr></thead>
                <tbody>
                  {gstr1.b2b?.map((r, i) => (
                    <tr key={i}>
                      <td className="font-mono text-sm">{r.invoice_number}</td>
                      <td className="text-sm text-slate-500">{r.invoice_date}</td>
                      <td className="text-sm">{r.party_name}</td>
                      <td className="font-mono text-xs">{r.gstin}</td>
                      <td className="text-sm">{r.place_of_supply}</td>
                      <td className="text-right font-mono text-sm">Rs.{formatCurrency(r.taxable_value)}</td>
                      <td className="text-right font-mono text-sm">{r.cgst > 0 ? `Rs.${formatCurrency(r.cgst)}` : '-'}</td>
                      <td className="text-right font-mono text-sm">{r.sgst > 0 ? `Rs.${formatCurrency(r.sgst)}` : '-'}</td>
                      <td className="text-right font-mono text-sm">{r.igst > 0 ? `Rs.${formatCurrency(r.igst)}` : '-'}</td>
                      <td className="text-right font-mono text-sm font-medium">Rs.{formatCurrency(r.invoice_value)}</td>
                    </tr>
                  ))}
                  {!gstr1.b2b?.length && <tr><td colSpan={10} className="text-center text-slate-400 py-6">No B2B invoices</td></tr>}
                </tbody>
              </table>
            </div>
          </Section>

          {/* B2C Section */}
          <Section title="B2C Invoices (Without GSTIN)" count={gstr1.b2c?.length} testId="gstr1-b2c">
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead><tr><th>Invoice No.</th><th>Date</th><th>Party</th><th>Place</th><th className="text-right">Taxable</th><th className="text-right">Tax</th><th className="text-right">Total</th></tr></thead>
                <tbody>
                  {gstr1.b2c?.map((r, i) => (
                    <tr key={i}>
                      <td className="font-mono text-sm">{r.invoice_number}</td>
                      <td className="text-sm text-slate-500">{r.invoice_date}</td>
                      <td className="text-sm">{r.party_name}</td>
                      <td className="text-sm">{r.place_of_supply}</td>
                      <td className="text-right font-mono text-sm">Rs.{formatCurrency(r.taxable_value)}</td>
                      <td className="text-right font-mono text-sm text-orange-700">Rs.{formatCurrency(r.total_tax)}</td>
                      <td className="text-right font-mono text-sm font-medium">Rs.{formatCurrency(r.invoice_value)}</td>
                    </tr>
                  ))}
                  {!gstr1.b2c?.length && <tr><td colSpan={7} className="text-center text-slate-400 py-6">No B2C invoices</td></tr>}
                </tbody>
              </table>
            </div>
          </Section>

          {/* HSN Summary */}
          <Section title="HSN Summary" count={gstr1.hsn?.length} testId="gstr1-hsn">
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead><tr><th>HSN Code</th><th>Description</th><th>UQC</th><th className="text-right">Qty</th><th className="text-right">Taxable</th><th className="text-right">Tax</th><th className="text-right">Total</th></tr></thead>
                <tbody>
                  {gstr1.hsn?.map((h, i) => (
                    <tr key={i}>
                      <td className="font-mono text-sm">{h.hsn_code}</td>
                      <td className="text-sm">{h.description}</td>
                      <td className="text-sm">{h.uqc}</td>
                      <td className="text-right font-mono text-sm">{h.total_quantity}</td>
                      <td className="text-right font-mono text-sm">Rs.{formatCurrency(h.taxable_value)}</td>
                      <td className="text-right font-mono text-sm text-orange-700">Rs.{formatCurrency(h.total_tax)}</td>
                      <td className="text-right font-mono text-sm font-medium">Rs.{formatCurrency(h.total_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </div>
      ) : tab === 'gstr3b' && gstr3b ? (
        <div className="space-y-4">
          <div className="flex gap-2">
            <button onClick={() => exportJSON(gstr3b, `GSTR3B_${period || 'all'}.json`)}
              className="btn-accent flex items-center gap-2 text-sm" data-testid="export-gstr3b-json">
              <Download className="w-4 h-4" /> Export GSTR-3B JSON
            </button>
          </div>

          {/* Outward Supplies */}
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="gstr3b-outward">
            <h3 className="font-heading font-semibold text-slate-800 mb-4">3.1 Outward Supplies (Sales)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { l: 'Invoices', v: gstr3b.outward_supplies?.invoice_count },
                { l: 'Taxable Value', v: `Rs.${formatCurrency(gstr3b.outward_supplies?.taxable_value)}` },
                { l: 'CGST', v: `Rs.${formatCurrency(gstr3b.outward_supplies?.cgst)}` },
                { l: 'SGST', v: `Rs.${formatCurrency(gstr3b.outward_supplies?.sgst)}` },
                { l: 'IGST', v: `Rs.${formatCurrency(gstr3b.outward_supplies?.igst)}` },
                { l: 'Total Tax', v: `Rs.${formatCurrency(gstr3b.outward_supplies?.total_tax)}`, c: 'text-orange-700' },
              ].map((m, i) => (
                <div key={i}>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase">{m.l}</p>
                  <p className={`text-base font-bold font-mono mt-1 ${m.c || 'text-slate-900'}`}>{m.v}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Input Tax Credit */}
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="gstr3b-itc">
            <h3 className="font-heading font-semibold text-slate-800 mb-4">4. Input Tax Credit (Purchases)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { l: 'Invoices', v: gstr3b.input_tax_credit?.invoice_count },
                { l: 'Taxable Value', v: `Rs.${formatCurrency(gstr3b.input_tax_credit?.taxable_value)}` },
                { l: 'CGST', v: `Rs.${formatCurrency(gstr3b.input_tax_credit?.cgst)}` },
                { l: 'SGST', v: `Rs.${formatCurrency(gstr3b.input_tax_credit?.sgst)}` },
                { l: 'IGST', v: `Rs.${formatCurrency(gstr3b.input_tax_credit?.igst)}` },
                { l: 'Total Credit', v: `Rs.${formatCurrency(gstr3b.input_tax_credit?.total_credit)}`, c: 'text-emerald-700' },
              ].map((m, i) => (
                <div key={i}>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase">{m.l}</p>
                  <p className={`text-base font-bold font-mono mt-1 ${m.c || 'text-slate-900'}`}>{m.v}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Net Tax Payable */}
          <div className="bg-orange-50 border border-orange-200 rounded-md p-5" data-testid="gstr3b-net">
            <h3 className="font-heading font-semibold text-orange-900 mb-4">6. Net GST Payable</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { l: 'CGST', v: `Rs.${formatCurrency(gstr3b.net_tax_payable?.cgst)}` },
                { l: 'SGST', v: `Rs.${formatCurrency(gstr3b.net_tax_payable?.sgst)}` },
                { l: 'IGST', v: `Rs.${formatCurrency(gstr3b.net_tax_payable?.igst)}` },
                { l: 'Total Net Payable', v: `Rs.${formatCurrency(gstr3b.net_tax_payable?.total)}`, c: 'text-orange-900 text-xl' },
              ].map((m, i) => (
                <div key={i}>
                  <p className="text-[10px] font-semibold text-orange-700 uppercase">{m.l}</p>
                  <p className={`font-bold font-mono mt-1 ${m.c || 'text-orange-800 text-base'}`}>{m.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
